import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataQuery from 'data/DataQuery';
import { Contract_FuelingCards } from 'data/endpoints';
import { useQuery } from 'data/hooks/useQuery';
import ConditionalExecute from 'data/models/ConditionalExecute';
import FuelingCardDTO from 'features/contracts/dtos/FuelingCardDTO';
import CardHeaderButton from 'features/shared/components/card/CardHeaderButton';
import PageCard from 'features/shared/components/card/PageCard';
import { useStateDataGrid } from 'features/shared/components/dataGrid/hooks/useStateDataGrid';
import NoContentWrapper from 'features/shared/components/noContentWrapper/NoContentWrapper';
import PagedDataResultDTO from 'features/shared/dtos/PagedDataResultDTO';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FuelingCard from '../components/fueling/FuelingCard';
import FuelingCardHeader from '../components/fueling/FuelingCardHeader';
import Pagination from 'features/shared/components/pagination/Pagination';
import { loadDataFilterConfig } from 'features/shared/helpers/modules';
import DataFilterModal from 'features/shared/components/dataFilterModal/DataFilterModal';

const fuelingCardsDataFilterJson = loadDataFilterConfig("contractDetailFuelingCardsDataFilter");
const pageSize = 5;

type Props = {
  fuelingContractId: string;
}

const FuelingCardsSection = ({ fuelingContractId }: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;

  // state
  const [wasOpenedFilter, setWasOpenedFilter] = useState(false);

  // data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler
  } = useStateDataGrid(pageSize, fuelingCardsDataFilterJson);

  // fetch data
  const conditionalExecute = () => {
    if (dataGrid.paginationUrlSearchParams) return true;
    if (dataGrid.filtrUrlSearchParams) return true;

    return false;
  };

  const cardsQuery: DataQuery = {
    url: Contract_FuelingCards,
    routeParams: getRouteParams("contractId", fuelingContractId),
    params: new URLSearchParams(dataGrid.computedUrlSearchParams),
  };
  const execute: ConditionalExecute = {
    apply: true,
    condition: conditionalExecute,
  };

  const { data: fuelingCards, loadStatus: fuelingCardsStatus } = useQuery<
    PagedDataResultDTO<FuelingCardDTO>
  >(
    cardsQuery,
    { result: [], count: 0 },
    [fuelingContractId, dataGrid.computedUrlSearchParams],
    { useTracker: true },
    execute
  );

  // handlers
  const openFilterHandler = () => {
    setShowFilter(true);
    if (!wasOpenedFilter) setWasOpenedFilter(true);
  };

  // sub-renders
  const renderFuelingCards = () => {
    return fuelingCards.result.map((item) => (
      <div className="py-2" key={item.cardNumber}>
        <FuelingCard 
          card={item}
        />
      </div>
    ));
  };

  const renderActionBtns = () => {
    let result: JSX.Element[] = [];

    if (fuelingCards.count > 1 || wasOpenedFilter) {
      let elem = (
        <CardHeaderButton
          type="button"
          onClick={openFilterHandler}
          key={fuelingContractId}
        >
          <FontAwesomeIcon icon="filter" />
        </CardHeaderButton>
      );
      result.push(elem);
    }

    return result;
  };


  // render
  return (
    <PageCard 
      title={tLabels("fuelingCardsSection.cardTitle")} 
      headerActionBtns={renderActionBtns}
    >
      <NoContentWrapper
        data={fuelingCards}
        loadStatus={[fuelingCardsStatus]}
      >
        <FuelingCardHeader />
        {renderFuelingCards()}
        <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between mt-2 mb-1 pt-3 border-top">
          <div>
            <span className="text-muted">{tLabels("fuelingCardsSection.cardsCount")} </span>
            {fuelingCards.count}
          </div>
          <Pagination 
            urlParams={new URLSearchParams(dataGrid.paginationUrlSearchParams)}
            count={fuelingCards.count}
            pageSize={pageSize}
            onPageChange={paginationPageChangeHandler}
            className="mt-2 mt-sm-0"
            useXsView
          />
        </div>
      </NoContentWrapper>
      {(fuelingCards.count > 1 || wasOpenedFilter) && (
        <DataFilterModal
          descriptors={descriptors}
          setDescriptors={setDescriptors}
          applyButtonOnClick={filterApplyHandler}
          resetButtonOnClick={filterResetHandler}
          show={showFilter}
          setShow={setShowFilter}
        />
      )}
    </PageCard>
  );
}

export default FuelingCardsSection;